import {useEffect, useRef, useState} from "react";
import {useApp} from "../app.context";
import {AudioEdit, decodeAudio, editedDuration, encodeWav, isIdentity, peaks, pitchRate, renderEdit} from "../utils/audioEdit";

interface SoundEditPanelProps {
    id: string;
    /** The sound's editor volume (0-100), so the preview sounds as it will in game. */
    volume: number;
    onDone(): void;
}

const WAVEFORM_HEIGHT = 70;

// Trim and pitch one sound, then bake the result into a new file (see utils/audioEdit). Applying uploads
// it through the same path as a drag-and-drop upload, so the file it replaces lands in the Archive tab.
function SoundEditPanel(props: SoundEditPanelProps) {
    const {getSoundUrl, uploadFile, setNotification} = useApp();
    const [source, setSource] = useState<AudioBuffer>();
    const [error, setError] = useState<string>();
    const [edit, setEdit] = useState<AudioEdit>({start: 0, end: 0, semitones: 0});
    const [busy, setBusy] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const previewRef = useRef<AudioContext | null>(null);
    const dragRef = useRef<'start' | 'end' | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const url = getSoundUrl(props.id);
            if (!url) {
                setError('No file uploaded yet.');
                return;
            }
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
                const buffer = await decodeAudio(await response.arrayBuffer());
                if (cancelled) return;
                setSource(buffer);
                setEdit({start: 0, end: buffer.duration, semitones: 0});
            } catch (e) {
                if (!cancelled) setError(`Could not load the sound: ${e}`);
            }
        })();
        return () => {
            cancelled = true;
            stopPreview();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !source) return;
        const ratio = window.devicePixelRatio || 1;
        const width = Math.max(1, Math.floor(canvas.clientWidth * ratio));
        const height = Math.floor(WAVEFORM_HEIGHT * ratio);
        canvas.width = width;
        canvas.height = height;
        const g = canvas.getContext('2d');
        if (!g) return;

        const style = getComputedStyle(canvas);
        const wave = style.getPropertyValue('--text-color').trim() || '#cfd6dd';
        const accent = style.getPropertyValue('--accent-color').trim() || '#e2b650';
        g.clearRect(0, 0, width, height);

        const values = peaks(source, width);
        g.fillStyle = wave;
        for (let x = 0; x < width; x++) {
            const h = Math.max(1, values[x] * (height - 2));
            g.fillRect(x, (height - h) / 2, 1, h);
        }

        // Dim what the trim cuts away, and mark the two cut points.
        const startX = edit.start / source.duration * width;
        const endX = edit.end / source.duration * width;
        g.fillStyle = 'rgba(0, 0, 0, 0.55)';
        g.fillRect(0, 0, startX, height);
        g.fillRect(endX, 0, width - endX, height);
        g.fillStyle = accent;
        g.fillRect(Math.max(0, startX - ratio), 0, 2 * ratio, height);
        g.fillRect(Math.min(width - 2 * ratio, endX - ratio), 0, 2 * ratio, height);
    }, [source, edit]);

    function stopPreview() {
        previewRef.current?.close().catch(() => undefined);
        previewRef.current = null;
        setPreviewing(false);
    }

    async function preview() {
        if (!source) return;
        if (previewing) {
            stopPreview();
            return;
        }
        const rendered = await renderEdit(source, edit);
        const context = new AudioContext();
        const node = context.createBufferSource();
        node.buffer = rendered;
        const gain = context.createGain();
        gain.gain.value = Math.max(0, Math.min(1, props.volume / 100));
        node.connect(gain).connect(context.destination);
        node.onended = () => {
            if (previewRef.current === context) stopPreview();
        };
        previewRef.current = context;
        setPreviewing(true);
        node.start();
    }

    async function apply() {
        if (!source || busy) return;
        setBusy(true);
        stopPreview();
        try {
            const rendered = await renderEdit(source, edit);
            const file = new File([encodeWav(rendered)], `${props.id}.wav`, {type: 'audio/wav'});
            if (await uploadFile(props.id, file)) {
                setNotification({
                    title: 'Sound updated',
                    message: 'The edited file is now in use. The previous one is in the Archive tab, where it can be restored. Save the branch to publish it.'
                });
                props.onDone();
            }
        } catch (e) {
            setNotification({title: 'Could not apply the edit', message: `${e}`});
        } finally {
            setBusy(false);
        }
    }

    // Click or drag on the waveform: moves whichever cut point is nearer.
    function timeAt(clientX: number): number {
        const canvas = canvasRef.current;
        if (!canvas || !source) return 0;
        const rect = canvas.getBoundingClientRect();
        return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width)) * source.duration;
    }

    function moveCut(which: 'start' | 'end', t: number) {
        if (!source) return;
        const minimum = Math.min(0.01, source.duration);
        setEdit(e => which === 'start'
            ? {...e, start: Math.min(t, e.end - minimum)}
            : {...e, end: Math.max(t, e.start + minimum)});
    }

    if (error) return <p style={{opacity: 0.6}}>{error}</p>;
    if (!source) return <p style={{opacity: 0.6}}>{'Loading sound...'}</p>;

    const unchanged = isIdentity(edit, source.duration);
    const rate = pitchRate(edit.semitones);
    const range = (value: number, onChange: (v: number) => void, min: number, max: number, step: number) =>
        <input type={'range'} min={min} max={max} step={step} value={value}
               onChange={e => onChange(Number(e.target.value))} style={{width: '100%'}}/>;

    return (
        <div className={'layout vertical gap'} style={{gap: 8}}>
            <p className={'text header'}>{'Trim & Pitch'}</p>
            <canvas ref={canvasRef}
                    style={{width: '100%', height: WAVEFORM_HEIGHT, cursor: 'ew-resize',
                        borderRadius: 'var(--border-radius)', border: 'solid 1px var(--overlay-color)'}}
                    onMouseDown={e => {
                        const t = timeAt(e.clientX);
                        const which = Math.abs(t - edit.start) <= Math.abs(t - edit.end) ? 'start' : 'end';
                        dragRef.current = which;
                        moveCut(which, t);
                    }}
                    onMouseMove={e => { if (dragRef.current) moveCut(dragRef.current, timeAt(e.clientX)); }}
                    onMouseUp={() => { dragRef.current = null; }}
                    onMouseLeave={() => { dragRef.current = null; }}/>
            <div className={'layout horizontal center gap'}>
                <p style={{width: 70}}>{'Start'}</p>
                {range(edit.start, v => moveCut('start', v), 0, source.duration, 0.01)}
                <p className={'label'} style={{width: 70, textAlign: 'right'}}>{`${edit.start.toFixed(2)} s`}</p>
            </div>
            <div className={'layout horizontal center gap'}>
                <p style={{width: 70}}>{'End'}</p>
                {range(edit.end, v => moveCut('end', v), 0, source.duration, 0.01)}
                <p className={'label'} style={{width: 70, textAlign: 'right'}}>{`${edit.end.toFixed(2)} s`}</p>
            </div>
            <div className={'layout horizontal center gap'}
                 title={'Like Unity\'s pitch: higher is also faster, lower also slower.'}>
                <p style={{width: 70}}>{'Pitch'}</p>
                {range(edit.semitones, v => setEdit(e => ({...e, semitones: v})), -12, 12, 0.5)}
                <p className={'label'} style={{width: 70, textAlign: 'right'}}>
                    {`${edit.semitones > 0 ? '+' : ''}${edit.semitones} st`}
                </p>
            </div>
            <p style={{opacity: 0.6}}>
                {`${source.duration.toFixed(2)} s → ${editedDuration(edit).toFixed(2)} s` +
                    (edit.semitones !== 0 ? `, ×${rate.toFixed(2)} speed` : '')}
            </p>
            <div className={'layout horizontal gap'}>
                <button onClick={preview}>{previewing ? 'Stop' : 'Preview'}</button>
                <button disabled={unchanged}
                        onClick={() => setEdit({start: 0, end: source.duration, semitones: 0})}>{'Reset'}</button>
                <button className={'flex-grow'} disabled={unchanged || busy} onClick={apply}>
                    {busy ? 'Applying...' : 'Apply'}
                </button>
                <button onClick={() => { stopPreview(); props.onDone(); }}>{'Close'}</button>
            </div>
        </div>
    );
}

export default SoundEditPanel;
